import { localized, t } from '@weavedev/lit-i18next';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import {
  getDownloadDatasheetsUrl,
  getGetProductImagesUrl,
  getGetProductVariantImagesUrl,
  getShowProductCertificateUrl,
  getShowProductVariants,
  ImageResolutionEnum,
} from '@/api-sdk';
import { iconPdf } from '@/components/icons';
import { resolutions } from '@/data/data.resolutions';
import { BREAKPOINT_MEDIUM_PX } from '@/settings';
import { baseStyles } from '@/styles';
import { SelectOption } from '@/types';
import { i18nInit, setBaseUrl } from '@/utils';

import './fg-product-download';

@customElement('fg-product-downloads')
@localized()
export class FgProductDownloads extends LitElement {
  @property({ type: String })
  locale = '';

  @property({ type: String })
  product = '';

  @property({ type: String })
  baseUrl = '';

  @state()
  private productCertificateLink: string | undefined = undefined;

  @state()
  private resolutions: SelectOption[] = [];

  @state()
  private colors: SelectOption[] = [];

  async openLink(link: string) {
    window.location.href = link;
  }

  async getProductDataSheets(event: CustomEvent) {
    const { colors } = event.detail;
    try {
      const datasheetsUrl = await getDownloadDatasheetsUrl({
        productCode: this.product,
        params: {
          locale: this.locale,
          'variantCodes[]': colors,
        },
      });
      await this.openLink(datasheetsUrl);
    } catch (error) {
      console.error(error);
    }
  }

  async getImages(event: CustomEvent) {
    const { hasColor, colors, resolution } = event.detail;
    const resolutions =
      resolution === 'all'
        ? [ImageResolutionEnum.low, ImageResolutionEnum.high]
        : [resolution as ImageResolutionEnum];
    if (hasColor) {
      try {
        const productVariantImagesUrl = await getGetProductVariantImagesUrl({
          code: this.product,
          params: {
            'resolutions[]': resolutions,
            'variantCodes[]': colors,
          },
        });
        await this.openLink(productVariantImagesUrl);
        return;
      } catch (error) {
        console.error(error);
      }
    }
    try {
      const link = await getGetProductImagesUrl({
        code: this.product,
        params: {
          'resolutions[]': resolutions,
        },
      });
      await this.openLink(link);
    } catch (error) {
      console.error(error);
    }
  }

  async getProductCertificateLink() {
    try {
      const productCertificateUrl = await getShowProductCertificateUrl({
        productCode: this.product,
      });
      this.productCertificateLink = productCertificateUrl?.url;
    } catch (error) {
      console.error(error);
    }
  }

  async getVariantColorCodes() {
    try {
      const variantColorCodes = await getShowProductVariants({
        productCode: this.product,
        params: {
          locale: this.locale,
        },
      });

      this.colors = variantColorCodes?.map(color => ({
        value: color.code,
        text: color.name,
        'sub-text': color.code,
      }));
    } catch (error) {
      console.error(error);
    }
  }

  async scheduleUpdate() {
    setBaseUrl(this.baseUrl);

    window.i18next = window.i18next || i18nInit(this.locale);
    await window.i18next;

    super.scheduleUpdate();
  }

  async firstUpdated() {
    await this.getVariantColorCodes();
    await this.getProductCertificateLink();

    this.resolutions = resolutions;
  }

  render() {
    const iconPdfTemplate = html`
      <div class="icon-pdf-wrapper">${iconPdf}</div>
    `;

    return html`
      <div>
        <div class="downloads-container">
          <fg-product-download
            part="wholesale"
            .colors=${this.colors}
            .resolutions=${this.resolutions}
            .downloadTitle=${'labels.productImagesTitle'}
            @download-file=${this.getImages}
          ></fg-product-download>
          <fg-product-download
            part="wholesale"
            .resolutions=${this.resolutions}
            .downloadTitle=${'labels.coverImagesTitle'}
            @download-file=${this.getImages}
          ></fg-product-download>
          <fg-product-download
            part="wholesale"
            .colors=${this.colors}
            .icon=${iconPdfTemplate}
            .downloadTitle=${'labels.productsheetsTitle'}
            @download-file=${this.getProductDataSheets}
          ></fg-product-download>
          ${this.productCertificateLink
            ? html`
                <fg-product-download
                  part="wholesale"
                  .icon=${iconPdfTemplate}
                  .downloadTitle=${'labels.productCertificateTitle'}
                  .downloadButtonLabel=${'labels.viewButton'}
                  .downloadLink=${this.productCertificateLink}
                ></fg-product-download>
              `
            : nothing}
        </div>
        <p class="only-desktop">${t('labels.onlyUsedDesktop')}</p>
      </div>
    `;
  }

  static styles = [
    baseStyles,
    css`
      .downloads-container {
        width: 100%;

        @media screen and (max-width: ${BREAKPOINT_MEDIUM_PX}px) {
          && {
            display: none;
          }
        }
      }

      .only-desktop {
        display: none;

        @media screen and (max-width: ${BREAKPOINT_MEDIUM_PX}px) {
          && {
            display: block;
            margin-top: 8px;
          }
        }
      }
    `,
  ];
}
